import type { RolePermissionName } from 'common/api/endpoints/companies';
import type { Feature, Integration } from 'common/constants/billing';

export const featureNames: Record<Feature, string> = {
  adminRoles: 'Admin Roles',
  advancedAnalytics: 'Advanced Analytics',
  autoResponse: 'Smart Replies',
  categories: 'Categories',
  changelogEmailSubscriptions: 'Changelog Email Subscriptions',
  commentSearch: 'Comment Searching',
  customAccess: 'Custom Board Access',
  customDomain: 'Custom Domains',
  customEmailDomain: 'Custom Email Domains',
  customPostFields: 'Custom Post Fields',
  customRoles: 'Custom Roles',
  customStatuses: 'Custom Statuses',
  deduplication: 'Deduplication',
  disableUserSubmissions: 'Disable User Submissions',
  exportComments: 'Export Comments',
  fileAttachments: 'File Attachments',
  internalComments: 'Internal Comments',
  knowledgeHub: 'Knowledge Hub',
  liveSupport: 'Priority Support',
  moderationTools: 'Moderation Tools',
  postAutomation: 'Post Automation',
  postETAs: 'Post ETAs',
  postOwners: 'Post Owners',
  prioritizationRoadmap: 'Roadmap Prioritization',
  privateAuthors: 'Private Authors',
  privateBoards: 'Private Boards',
  privateChangelog: 'Private Changelog',
  privateComments: 'Private Comments',
  privateVotes: 'Private Votes',
  removeBranding: 'Remove Branding',
  reviewSources: 'Review Sources',
  mcp: 'MCP connectors',
  shareRoadmaps: 'Share Roadmaps',
  summarizeComments: 'Summarize Comments',
  supportExtraction: 'Support Extraction',
  tags: 'Tags',
  translateContent: 'Translate Content',
  unlistedBoards: 'Unlisted Boards',
  userProfiles: 'User Profiles',
  userSegmentation: 'User Segmentation',
  voteWeights: 'Vote weights',
};

export const integrationNames: Record<Integration, string> = {
  api: 'API',
  asana: 'Asana',
  azureAD: 'Azure AD',
  azureDevops: 'Azure DevOps',
  browserExtension: 'Browser Extension',
  clickup: 'Clickup',
  discord: 'Discord',
  freshdesk: 'Freshdesk',
  github: 'GitHub',
  gong: 'Gong',
  gsuite: 'Google Workspace',
  helpscout: 'Help Scout',
  hubspot: 'HubSpot',
  intercom: 'Intercom',
  jira: 'Jira',
  linear: 'Linear',
  microsoftTeams: 'Microsoft Teams',
  oidc: 'OIDC',
  okta: 'Okta',
  oneLogin: 'OneLogin',
  salesforce: 'Salesforce',
  segment: 'Segment',
  slack: 'Slack',
  tldv: 'tl;dv',
  webhooks: 'Webhooks',
  zapier: 'Zapier',
  zendesk: 'Zendesk',
  zoom: 'Zoom',
};

export const permissionNames: Record<RolePermissionName, string> = {
  changeIdeaFields: 'Change idea fields',
  changeIdeaOwner: 'Change idea owner',
  changeIdeaStatus: 'Change idea status',
  createIdeas: 'Create ideas',
  createInsights: 'Create insights',
  changePostETA: 'Change post ETA',
  changePostOwner: 'Change post owner',
  changePostStatus: 'Change post status',
  changePostTags: 'Change post tags',
  createPublicComment: 'Create public comments',
  customizeBoards: 'Customize "Create Post" forms',
  deleteIdeas: 'Delete ideas',
  deleteComments: 'Delete comments',
  deleteInstance: 'Delete company instance',
  deletePosts: 'Delete posts',
  deleteUsers: 'Delete users',
  exportData: 'Export data',
  importPosts: 'Import posts',
  manageAPIKey: 'Manage API key',
  manageAuthSettings: 'Manage authentication settings',
  manageBilling: 'Manage billing',
  manageBoards: 'Create, delete & update boards',
  manageBoardPrivacy: 'Manage board privacy',
  manageCategories: 'Create/delete categories',
  manageChangelog: 'Manage changelog',
  manageChangelogPrivacy: 'Manage changelog privacy',
  manageCompanyProfile: 'Manage company profile',
  manageCustomDomains: 'Manage custom domains',
  manageCustomPostFields: 'Manage post fields',
  manageEmailSettings: 'Manage email settings',
  manageGroups: 'Manage groups',
  manageQueue: 'Manage inbox settings',
  manageIntegrations: 'Manage integrations',
  manageIdeaFields: 'Manage idea fields',
  manageInsights: 'Manage insights',
  managePostAutomation: 'Manage post automation',
  manageRoadmap: 'Manage roadmaps',
  manageViews: 'Manage views',
  manageSSOKey: 'Manage SSO key',
  manageStatuses: 'Create/delete post statuses',
  manageTags: 'Create/delete post tags',
  manageTeammates: 'Manage teammates',
  manageUserSegmentation: 'Manage user segments',
  manageWebhooks: 'Manage webhooks',
  mergePosts: 'Merge/unmerge posts',
  mergeIdeas: 'Merge/unmerge ideas',
  moveIdeas: 'Move ideas',
  pinComments: 'Pin comments',
  summarizeComments: 'Summarize comments',
  viewUserDetails: 'View user details',
  voteOnBehalf: 'Vote on behalf of others',
};

export const syncJobs = [
  // Autopilot client-data syncs
  'FreshdeskTicketSyncManager',
  'gong-CallSyncManager',
  'HelpscoutConversationSyncManager',
  'IntercomConversationSyncManager',
  'ReviewSourcesSyncManager',
  'tldv-CallSyncManager',
  'ZendeskTicketSyncManager',
  'zoom-CallSyncManager',

  // Autopilot internal
  'AutopilotTaskQueueSyncManager',
  'AutoResponseSyncManager',
  'CompanyAICreditResetManager',
  'FeatureSyncManager',
  'IdeaThemesGenerationProducer',
  'QueueItemSyncManager',

  // Client-data syncs
  'AsanaWebhookSyncManager',
  'ClickupDataSyncManager',
  'IntercomSyncManager',
  'OpportunitySyncManager',

  // Internal data jobs
  'CannyHubSpotSyncManager',
  'CompanyDeletionLifecycleProducer',
  'IDBCalculatedFieldsProducer',
  'LastThirtyDayVoteCountSyncManager',
  'RecalculateMRRManager',
  'RecalculatePostOpportunityValuesManager',
  'SimplesatSyncManager',
  'SSLCertRenewalManager',
  'UserLookupsSyncManager',

  // Notification jobs
  'AdminInviteSyncManager',
  'DelinquentNotificationManager',
  'TeamReportsManager',
  'UserReportsManager',

  // Producers
  'SalesforceSyncProducer',
] as const;
